import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useChallengeSystem } from '@/hooks/useChallengeSystem';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export function ChallengeNotifications() {
  const { user } = useAuth();
  const { acceptChallenge, declineChallenge } = useChallengeSystem();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('challenge_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'room_invitations',
          filter: `invitee_id=eq.${user.id}`,
        },
        async (payload) => {
          const invitation = payload.new as any;
          
          // Only show toast for game challenges
          if (invitation.challenge_type === 'game_challenge') {
            // Get inviter username
            const { data: inviterProfile } = await supabase
              .from('user_profiles')
              .select('username, display_name')
              .eq('id', invitation.inviter_id)
              .single();

            const inviterName = inviterProfile?.username || inviterProfile?.display_name || 'Someone';

            toast({
              title: "⚔️ New Challenge!",
              description: `${inviterName} challenges you to a game!`,
              action: (
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={async () => {
                      await acceptChallenge(invitation.id);
                      // Update game status to ongoing
                      if (invitation.game_session_id) {
                        await supabase
                          .from('game_sessions')
                          .update({ game_status: 'ongoing' })
                          .eq('id', invitation.game_session_id);
                        
                        // Navigate directly to game
                        navigate(`/?game=${invitation.game_session_id}`);
                      }
                    }}
                  >
                    Accept
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => declineChallenge(invitation.id)}
                  >
                    Decline
                  </Button>
                </div>
              ),
              duration: 15000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, acceptChallenge, declineChallenge, navigate]);

  return null;
}
